import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { Clock, CheckCircle, DollarSign, Smile, Users, TrendingUp, BarChart3, ArrowRight } from 'lucide-react'
import KpiCard from '../components/KpiCard'
import LoadingSpinner from '../components/LoadingSpinner'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import {
  calculateRealisedKPIs,
  calculateTotalForecastHrs,
  getTierColor,
  statusConfig,
  calculateAverageSatisfaction,
  projectAdoptionPct,
  getLateProjects,
} from '../lib/utils'
import type { Project, SurveyResponse, Settings } from '../types'

// Compact "12.3K" / "1.2M" formatter for the Hrs Saved tile detail line.
const compact = (v: number) =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(1)}K` : `${v}`

// How many Recent Projects rows to show: enough to fill the space from the
// table down to the bottom of the viewport, so a tall monitor shows more than
// a laptop. Measures the tbody's position + a real row's height and recomputes
// on resize. `ready` gates measurement until the table is actually rendered
// (the page shows a spinner while data loads). Falls back to `minRows`.
function useFillRowCount(ready: boolean, minRows = 5) {
  const ref = useRef<HTMLTableSectionElement>(null)
  const [count, setCount] = useState(minRows)
  useEffect(() => {
    if (!ready) return
    const compute = () => {
      const el = ref.current
      if (!el) return
      const top = el.getBoundingClientRect().top
      const rowHeight = el.querySelector('tr')?.getBoundingClientRect().height || 64
      const available = window.innerHeight - top - 24 // leave a little breathing room
      setCount(Math.max(minRows, Math.floor(available / rowHeight)))
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [ready, minRows])
  return { ref, count }
}

export default function Dashboard() {
  useDocumentTitle('Dashboard')
  const api = useApi()
  const { data, loading } = useData(() =>
    Promise.all([api.getProjects(), api.getSurveyResponses(), api.getSettings()]).then(([projects, responses, settings]) => ({ projects, responses, settings }))
  )
  // Recent Projects fills the remaining viewport height (min 5 rows).
  const { ref: recentBodyRef, count: recentCount } = useFillRowCount(!loading && !!data)

  if (loading || !data) return <LoadingSpinner />

  const { projects, responses, settings } = data as { projects: Project[]; responses: SurveyResponse[]; settings: Settings }
  const completed = projects.filter(p => p.status === 'complete')
  const forecastProjects = projects.filter(p => p.status === 'forecast')
  const kpis = calculateRealisedKPIs(completed, responses, settings.onTimeDays, settings.atCostPct)
  const totalForecastHrs = calculateTotalForecastHrs(completed, forecastProjects)

  const totalProjects = projects.length
  const withResponses = projects.filter(p => responses.some(r => r.projectId === p.id)).length
  const awaitingFeedback = projects.filter(p => !!p.surveySentAt && !responses.some(r => r.projectId === p.id)).length
  const closedProjects = projects.filter(p => p.status === 'complete').length
  // Denominator = projects where a survey was actually sent. Dividing by every
  // project (incl. forecast-status ones that never get a survey) would dilute
  // the rate and make healthy feedback look poor.
  const surveysSent = projects.filter(p => !!p.surveySentAt).length
  const responseRate = surveysSent > 0 ? `${Math.round((withResponses / surveysSent) * 100)}%` : '0%'
  const lateCount = getLateProjects(projects).length

  const kpiCards = [
    {
      title: 'On Time',
      value: `${kpis.onTime}%`,
      icon: Clock,
      color: 'blue' as const,
      info: (
        <>
          <strong>On Time</strong>
          <p>% of completed projects where actual go-live was within <strong>{settings.onTimeDays} days</strong> of the due date.</p>
          <p className="text-slate-300">Tolerance set in Settings.</p>
        </>
      ),
    },
    {
      title: 'In Full',
      value: `${kpis.inFull}%`,
      icon: CheckCircle,
      color: 'emerald' as const,
      info: (
        <>
          <strong>In Full</strong>
          <p>% of completed projects where the delivery lead marked <strong>Delivered In Full = Yes</strong>.</p>
        </>
      ),
    },
    {
      title: 'At Cost',
      value: `${kpis.atCost}%`,
      icon: DollarSign,
      color: 'violet' as const,
      info: (
        <>
          <strong>At Cost</strong>
          <p>% of completed projects where actual cost was within <strong>{settings.atCostPct}%</strong> of forecast cost.</p>
          <p className="text-slate-300">Projects without an actual cost are excluded (no penalty).</p>
        </>
      ),
    },
    {
      title: 'Satisfaction',
      value: kpis.satisfaction || 0,
      icon: Smile,
      color: 'cyan' as const,
      subtitle: '/5',
      detail: surveysSent > 0
        ? `Sol ${kpis.techSat.toFixed(1)} · Team ${kpis.peopleSat.toFixed(1)}\n${responseRate} response rate`
        : `Solution ${kpis.techSat.toFixed(1)}  |  Team ${kpis.peopleSat.toFixed(1)}`,
      info: (
        <>
          <strong>Satisfaction</strong>
          <p>Average 1-5 score across all survey responses. Solution and Team sub-scores are averaged together per respondent.</p>
          <p className="text-slate-300">Response rate = projects with a response ÷ projects where a survey was sent.</p>
        </>
      ),
    },
    {
      title: 'Adoption',
      value: `${kpis.adoption}%`,
      icon: Users,
      color: 'amber' as const,
      info: (
        <>
          <strong>Adoption</strong>
          <p>Sum of actual users ÷ sum of expected users across all completed projects (then capped at 100%).</p>
          <p className="text-slate-300">Percent-mode projects are back-calculated to a user count. Projects marked n/a are excluded.</p>
        </>
      ),
    },
    {
      title: 'Hrs Saved',
      value: compact(kpis.hrsSaved),
      icon: TrendingUp,
      color: 'rose' as const,
      detail: `+${compact(totalForecastHrs)} forecast`,
      info: (
        <>
          <strong>Hrs Saved</strong>
          <p><strong>Realised</strong> = actual hours from completed project ledgers, up to the current month.</p>
          <p><strong>Forecast</strong> = remaining ledger months on completed projects + expected value on forecast projects.</p>
        </>
      ),
    },
  ]

  const quickStats = [
    { value: totalProjects, color: 'slate', label: 'Total Projects' },
    { value: awaitingFeedback, color: 'amber', label: 'Awaiting Feedback' },
    { value: withResponses, color: 'blue', label: 'Has Responses' },
    { value: closedProjects, color: 'emerald', label: 'Closed' },
    { value: responseRate, color: 'violet', label: 'Response Rate' },
    { value: lateCount, color: lateCount > 0 ? 'red' : 'emerald', label: 'Late Forecasts' },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <Link to="/projects/new" className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          + Add Project
        </Link>
      </div>

      <div className="grid grid-cols-6 gap-3 mb-5">
        {kpiCards.map(card => <KpiCard key={card.title} {...card} />)}
      </div>

      <div className="grid grid-cols-6 gap-3 mb-5">
        {quickStats.map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center gap-3">
            <div className={`w-10 h-10 bg-${s.color}-100 rounded-lg flex items-center justify-center text-${s.color}-600 font-bold text-sm`}>
              {s.value}
            </div>
            <div className="text-sm text-slate-600">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-[#005EB8]/5 to-[#00AEEF]/5 border border-[#00AEEF]/20 rounded-xl p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
          </div>
          <div>
            <div className="font-semibold text-slate-800">Detailed Reports</div>
            <div className="text-sm text-slate-500">Charts, filters, and export options</div>
          </div>
        </div>
        <Link to="/reports" className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          View Reports <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Recent Projects</h3>
          <Link to="/projects" className="text-sm font-semibold text-[#00AEEF] hover:text-[#005EB8]">View all →</Link>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-[#00AEEF]/5 to-[#005EB8]/5 border-b border-slate-100">
              {['Project', 'Tier', 'Status', 'Satisfaction', 'Adoption'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody ref={recentBodyRef}>
            {[...projects]
              .sort((a, b) => (b.lastMeaningfulUpdate ?? b.dueDate ?? '').localeCompare(a.lastMeaningfulUpdate ?? a.dueDate ?? ''))
              .slice(0, recentCount)
              .map(project => {
              const projectResponses = responses.filter(r => r.projectId === project.id)
              const avgSat = calculateAverageSatisfaction(projectResponses)
              const adoptionPct = projectAdoptionPct(project)

              return (
                <tr key={project.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-5 py-4">
                    <Link to={`/projects/${project.id}`} className="font-semibold text-slate-800 hover:text-[#005EB8]">{project.name}</Link>
                    <span className="inline-block mt-1 ml-2 px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full">{project.subPortfolio}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getTierColor(project.tier)}`}>{project.tier}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusConfig[project.status]?.style}`}>
                      {statusConfig[project.status]?.label}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {avgSat ? <span className="font-bold text-[#00ACED]">{avgSat}/5</span> : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="px-5 py-4">
                    {adoptionPct !== null ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[80px]">
                          <div className="h-full bg-[#00843D] rounded-full" style={{ width: `${adoptionPct}%` }} />
                        </div>
                        <span className="text-sm font-medium text-slate-600">{adoptionPct}%</span>
                      </div>
                    ) : <span className="text-slate-400">-</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
